(() => {
  const rootSelector = '[data-aonsk-info-popover]';
  const accordionSelector = '[data-aonsk-seed-accordions] details, [data-aonsk-tomato-accordions] details';
  let activeRoot = null;
  let activePanel = null;
  let activeTrigger = null;
  let originalParent = null;
  let originalNext = null;
  let lastScrollY = window.scrollY;
  let scrollFrame = 0;
  let alignmentLockUntil = 0;

  const getHeaderOffset = () => {
    let offset = 18;
    const candidates = document.querySelectorAll(
      '.shopify-section-header-sticky, .header-wrapper, sticky-header, [data-sticky-header]'
    );
    candidates.forEach((element) => {
      const style = window.getComputedStyle(element);
      if (style.position !== 'fixed' && style.position !== 'sticky') return;
      const rect = element.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom > 0 && rect.top <= 1) offset = Math.max(offset, rect.bottom + 14);
    });
    return Math.min(offset, Math.max(18, window.innerHeight * 0.24));
  };

  const restorePanel = () => {
    if (!activePanel || !originalParent) return;
    if (originalNext && originalNext.parentNode === originalParent) originalParent.insertBefore(activePanel, originalNext);
    else originalParent.appendChild(activePanel);
    activePanel.classList.remove('is-portal');
    activePanel.style.removeProperty('--aonsk-popover-left');
    activePanel.style.removeProperty('--aonsk-popover-top');
  };

  const closePopover = (restoreFocus = false) => {
    if (!activeRoot || !activePanel || !activeTrigger) return;
    activePanel.hidden = true;
    activeTrigger.setAttribute('aria-expanded', 'false');
    activeRoot.classList.remove('is-open');
    restorePanel();
    const triggerToFocus = activeTrigger;
    activeRoot = null;
    activePanel = null;
    activeTrigger = null;
    originalParent = null;
    originalNext = null;
    if (restoreFocus) triggerToFocus.focus();
  };

  const positionPanel = () => {
    if (!activePanel || !activeTrigger) return;
    const triggerRect = activeTrigger.getBoundingClientRect();
    const panelRect = activePanel.getBoundingClientRect();
    const margin = 10;
    let left = triggerRect.left + (triggerRect.width / 2) - (panelRect.width / 2);
    left = Math.max(12, Math.min(left, window.innerWidth - panelRect.width - 12));
    let top = triggerRect.bottom + margin;
    if (top + panelRect.height > window.innerHeight - 12) top = triggerRect.top - panelRect.height - margin;
    top = Math.max(12, Math.min(top, window.innerHeight - panelRect.height - 12));
    activePanel.style.setProperty('--aonsk-popover-left', `${Math.round(left)}px`);
    activePanel.style.setProperty('--aonsk-popover-top', `${Math.round(top)}px`);
  };

  const openPopover = (root) => {
    const trigger = root && root.querySelector('[data-aonsk-info-trigger]');
    const panel = root && root.querySelector('[data-aonsk-info-panel]');
    if (!trigger || !panel) return;
    closePopover(false);
    activeRoot = root;
    activePanel = panel;
    activeTrigger = trigger;
    originalParent = panel.parentNode;
    originalNext = panel.nextSibling;
    document.body.appendChild(panel);
    panel.classList.add('is-portal');
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    root.classList.add('is-open');
    requestAnimationFrame(positionPanel);
  };

  const findStableViewportAnchor = (closingDetails, offset) => {
    const y = Math.max(1, Math.min(window.innerHeight - 2, offset + 8));
    const points = [window.innerWidth * 0.5, window.innerWidth * 0.28, window.innerWidth * 0.72];
    for (const x of points) {
      let candidate = document.elementFromPoint(x, y);
      if (!candidate) continue;
      if (closingDetails.contains(candidate)) continue;
      const fixedParent = candidate.closest('[style*="position: fixed"], .shopify-section-header-sticky, .header-wrapper');
      if (fixedParent) continue;
      return candidate;
    }
    const next = closingDetails.nextElementSibling;
    return next ? (next.querySelector('summary') || next) : null;
  };

  const closeDetailsWithoutJump = (details) => {
    if (!details || !details.open) return;
    const offset = getHeaderOffset();
    const anchor = findStableViewportAnchor(details, offset);
    const beforeTop = anchor && document.contains(anchor) ? anchor.getBoundingClientRect().top : null;
    details.open = false;
    requestAnimationFrame(() => {
      if (!anchor || !document.contains(anchor) || beforeTop === null) return;
      const afterTop = anchor.getBoundingClientRect().top;
      const delta = afterTop - beforeTop;
      if (Number.isFinite(delta) && Math.abs(delta) > 0.5) {
        window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
      }
    });
  };

  const alignOpenedAccordion = (details) => {
    if (!details || !details.open) return;
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    const offset = getHeaderOffset();
    const targetTop = window.scrollY + summary.getBoundingClientRect().top - offset;
    alignmentLockUntil = performance.now() + 550;
    window.scrollTo({ top: Math.max(0, targetTop), left: 0, behavior: 'auto' });
  };

  const closePassedAccordions = () => {
    const currentY = window.scrollY;
    const movingDown = currentY > lastScrollY + 1;
    lastScrollY = currentY;
    if (!movingDown || performance.now() < alignmentLockUntil) return;

    const offset = getHeaderOffset();
    const openAccordions = Array.from(document.querySelectorAll(accordionSelector)).filter((details) => details.open);
    for (const details of openAccordions) {
      const rect = details.getBoundingClientRect();
      if (rect.bottom <= offset + 2) {
        closeDetailsWithoutJump(details);
        break;
      }
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-aonsk-info-trigger]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      const root = trigger.closest(rootSelector);
      if (activeRoot === root) closePopover(false);
      else openPopover(root);
      return;
    }

    const closeButton = event.target.closest('[data-aonsk-info-close]');
    if (closeButton) {
      event.preventDefault();
      closePopover(true);
      return;
    }

    if (activePanel && !activePanel.contains(event.target)) closePopover(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePopover(true);
  });

  document.addEventListener('toggle', (event) => {
    const opened = event.target;
    if (!(opened instanceof HTMLDetailsElement) || !opened.open || !opened.matches(accordionSelector)) return;
    const group = opened.closest('[data-aonsk-seed-accordions], [data-aonsk-tomato-accordions]');
    if (!group) return;

    group.querySelectorAll('details[open]').forEach((other) => {
      if (other !== opened) other.open = false;
    });
    closePopover(false);

    /* Wait for the previous section to collapse, then put the newly opened
       summary exactly below the sticky header. This prevents it from jumping
       upward and leaving the reader halfway through the section. */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => alignOpenedAccordion(opened));
    });
  }, true);

  window.addEventListener('scroll', () => {
    closePopover(false);
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      closePassedAccordions();
    });
  }, { passive: true });

  window.addEventListener('resize', () => closePopover(false), { passive: true });
  window.addEventListener('orientationchange', () => closePopover(false), { passive: true });
  document.addEventListener('shopify:section:unload', () => closePopover(false));
})();
